/*
 * @ngdoc service
 * @name pubsubService
 * @module flowingly.services
 *
 * @description A service for publishing and subcribing to events.
 *
 * ## Notes
 * Events are only fired if:
 * * The event exists in the eventNames array
 * * There is at least one subscriber to that event
 * 
 * ###API
 * * subscriptions - list of all subcribers, organised by event name
 * * subscribe - registers a callback method to be called when the named event is publish
 * * publish - publish an event of given name
 * 
 * ###Usage
 *    
 * pubsubService.subscribe('WORKFLOW_PUBLISHED', onWorkflowPublished, 'workflow.canvas.controller');
 * pubsubService.publish('WORKFLOW_PUBLISHED', {});
 *
 * pubsubService.subscribe('FORM_TABLE_VALID', clearTableError, 'runnerCardController', $ctrl.formErrors);
 * 
 *     function setTableError(event, data, param) {
                  param.table = undefined;
                  param.table = true;
       }
*/
import {
  Callback,
  PendingEvent,
  PubSubEvent,
  Subscriber,
  allPubSubEvents,
  isSignalrEvent,
  isSystemEvent
} from '@Shared.Angular/@types/pubSub';
import { Services } from '@Shared.Angular/@types/services';
import angular, { ITimeoutService } from 'angular';

export default class PubsubService {
  private subscriptions:
    | Record<PubSubEvent, Subscriber<unknown>[]>
    | Record<string, never> = {};
  private debouncedEventTimeoutIds = {}; // keep track of timeoutIds of events being published
  private isConnectionReady = false;
  private pendingEvents: PendingEvent[] = [];

  constructor(
    private $timeout: ITimeoutService,
    private lodashService: Lodash,
    private devLoggingService: Services.DevLoggingService
  ) {
    const subscriberId = 'runner.signalR.client';
    const signalrConnected = () => {
      this.isConnectionReady = true;
      //execute all pending publish as now connection is established
      this.pendingEvents.forEach(({ event, data }) =>
        this.publish(event, data)
      );
      this.pendingEvents = [];
    };
    const signalrFailed = () => (this.isConnectionReady = false);

    //we first register known events that can be subscribed to
    allPubSubEvents.forEach((event) => (this.subscriptions[event] = []));
    this.subscribe('SIGNALR_CONNECTED', signalrConnected, subscriberId);
    this.subscribe('SIGNALR_CONNECT_FAILED', signalrFailed, subscriberId);
  }

  /**
   * Subscribe Late to an event. Late subscriptions will be called after all normal subscriptions.
   * @param event - the event name to subscribe to
   * @param callback - method to call when this event is published
   * @param subscriberId - subscriber id to prevent multiple subscriptions
   * @param param - optional param that will be passed to the callback method when it is invoked
   */
  subscribeLate<T>(
    event: PubSubEvent,
    callback: Callback<T>,
    subscriberId: string,
    param?: unknown
  ) {
    this.subscribe(event, callback, subscriberId, param, true);
  }

  /**
   * Subscribe Late to events. Late subscriptions will be called after all normal subscriptions.
   * @param events - the event names to subscribe to
   * @param callback - method to call when this event is published
   * @param subscriberId - subscriber id to prevent multiple subscriptions
   * @param param - optional param that will be passed to the callback method when it is invoked
   */
  subscribeManyLate<T>(
    events: PubSubEvent[],
    callback: Callback<T>,
    subscriberId: string,
    param?: unknown
  ) {
    events.forEach((event) => {
      this.subscribe(event, callback, subscriberId, param, true);
    });
  }

  /**
   * Subscribe to events.
   * @param events - the event names to subscribe to
   * @param callback - method to call when this event is published
   * @param subscriberId - subscriber id to prevent multiple subscriptions
   * @param param - optional param that will be passed to the callback method when it is invoked
   */
  subscribeMany<T>(
    events: PubSubEvent[],
    callback: Callback<T>,
    subscriberId: string,
    param?: unknown,
    isDelayed = false
  ) {
    events.forEach((event) => {
      this.subscribe(event, callback, subscriberId, param, isDelayed);
    });
  }

  /**
   * Subscribe to an event.
   * @param event - the event name to subscribe to
   * @param callback - method to call when this event is published
   * @param subscriberId - subscriber id to prevent multiple subscriptions
   * @param param - optional param that will be passed to the callback method when it is invoked
   * @param isDelayed - if true, the subscriber will be called after a delay
   */
  subscribe<T>(
    event: PubSubEvent,
    callback: Callback<T>,
    subscriberId: string,
    param?: unknown,
    isDelayed = false
  ) {
    //allow subscription only for known events

    const subscriber: Subscriber<T> = {
      id: subscriberId,
      callback,
      param,
      isDelayed
    };
    const subsribers = this.subscriptions[event];
    //If an id was provided, remove the old subscriptionso that it can be re-added
    //It is important we re-add it as the previous subscription might relate to an object that has been destroyed
    if (subscriberId) {
      this.lodashService.pullAllBy(subsribers, [{ id: subscriberId }], 'id');
    }
    //subscribe
    subsribers.push(subscriber);
  }

  /**
   * Execute all callbacks subsribed to the event
   * @param event
   * @param data
   */
  execCallbacks(event: PubSubEvent, data: unknown) {
    //capture this state of the subscriptions before timeout
    //in case they change before timeout activates (on state chnage);
    const subscribers = this.subscriptions[event];

    this.$timeout(() => {
      this.devLoggingService.info('publishing event ' + event);

      subscribers.forEach((subscriber) => {
        const { id, callback, param, isDelayed } = subscriber;

        if (
          (this.isConnectionReady || !isSignalrEvent(event)) &&
          angular.isFunction(callback)
        ) {
          try {
            //call the callback registered with the event & data
            this.devLoggingService.info(
              `subscriber ${id} for event ${event} executing`
            );
            if (isDelayed) {
              this.$timeout(() => callback(event, data, param), 100);
            } else {
              callback(event, data, param);
            }
          } catch (e) {
            this.devLoggingService.error(e);
          }
        } else {
          if (!this.isConnectionReady) {
            // pending list contains non-duplicated event only
            const pendingEvent = this.pendingEvents.find(
              (evt) => evt.event === event
            );
            if (pendingEvent) {
              pendingEvent.data = data;
            } else {
              this.pendingEvents.push({
                event,
                data
              });
            }
          }
        }
      });
    }, 0);
  }

  /**
   * Publish an event to all subscribers
   * systemEvent no debounced when publish
   * signalR event will be debounced by 1.5s
   * other non system events will be debounced by 300ms
   */
  publish(event: PubSubEvent, data: unknown = null, isImmediate = false) {
    // no debouncing for system events
    if (isSystemEvent(event) || isImmediate) {
      this.execCallbacks(event, data);
      return;
    }

    let timeoutId = this.debouncedEventTimeoutIds[event];

    if (timeoutId) {
      this.$timeout.cancel(timeoutId);
    }

    const waitMs = isSignalrEvent(event) ? 1500 : 300;

    // delay executing the callback by waitMs
    timeoutId = this.$timeout(() => {
      this.debouncedEventTimeoutIds[event] = null;
      // the wait is over. executing subscriber callback for the event
      this.execCallbacks(event, data);
    }, waitMs);

    this.debouncedEventTimeoutIds[event] = timeoutId;
  }

  //finds all subscriptions across all events that match this id and removes them
  unsubscribeAll(subscriberId: string) {
    for (const event in this.subscriptions) {
      const subscribers = this.subscriptions[event as PubSubEvent];
      if (subscribers.length > 0) {
        this.lodashService.pullAllBy(subscribers, [{ id: subscriberId }], 'id');
      }
    }
  }
}

angular
  .module('flowingly.services')
  .factory('pubsubService', [
    '$timeout',
    'lodashService',
    'devLoggingService',
    (
      $timeout: ITimeoutService,
      lodashService: Lodash,
      devLoggingService: Services.DevLoggingService
    ) => new PubsubService($timeout, lodashService, devLoggingService)
  ]);
export type PubSubServiceType = InstanceType<typeof PubsubService>;
